import React, { createContext, useContext, useState, useEffect } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import FoodAllowanceForm from "./FoodAllowanceForm";
import FoodAllowanceView from "./FoodAllowanceView";
import { useCombinedContext } from "../../context/CombinedContext";
import exportToExcel from "../Excel/ExportToExcel";
import FoodAllowanceRepayment from "./FoodAllowanceRepayment";
import ViewReport from "./FoodAllowanceReport";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import FoodPaymentReport from "./FoodPaymentReport";

const FoodAllowanceContext = createContext();

const FoodAllowanceList = ({ children }) => {
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const [foodallowance, setFoodAllowance] = useState([]);
  const [employee, setEmployee] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // For year-wise filtering
  const [selectedFromMonth, setSelectedFromMonth] = useState(""); // For month range filtering
  const [selectedToMonth, setSelectedToMonth] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedFoodDetail, setSelectedFoodDetail] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showPaymentReport, setShowPaymentReport] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isViewMode, setIsViewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { selectesEmployee, selectedCompany } = useCombinedContext();
  const companyId = selectedCompany?._id;
  const companyCode = selectedCompany?.company_code;

  // New state for status confirmation modal
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [statusFoodAllowance, setStatusFoodAllowance] = useState(null);
  // console.log("deleteTarget",deleteTarget)
  // console.log("filterData", filterData);
  // Array of month options with full names.
  const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];
  // console.log(foodallowance, "FoodAllowance");
  // Fetch food allowance data using the selected filters.
  const fetchFoodAllowance = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedYear) params.fromYear = selectedYear;
      if (selectedFromMonth) params.fromMonth = selectedFromMonth;
      if (selectedToMonth) params.toMonth = selectedToMonth;
      const response = await axiosInstance.get(
        `/foodallowance/report/all/${companyId}`,
        { params },
      );
      setFoodAllowance(response.data.data || []);
      setFilterData(response.data.data || []);
      // console.log("response for food allowance", response.data.data);
      // console.log("response for food allowance", filterData);
    } catch (error) {
      toast.error("Error fetching foodallowance");
    } finally {
      setLoading(false);
    }
  };

  // Fetch employees if needed.
  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/foodallowance/employee/${companyId}`,
      );
      setEmployee(response.data.employees || []);
    } catch (error) {
      toast.error("Error fetching Employee");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchFoodAllowance();
    }
  }, [companyId, selectedYear, selectedFromMonth, selectedToMonth]);

  // Client-side search filter.
  const handleSearchChange = (e) => {
    const searchText = e.target.value.toLowerCase();
    const filtered = foodallowance.filter(
      (fa) =>
        (fa.employee?.name &&
          fa.employee.name.toLowerCase().includes(searchText)) ||
        (fa.employee?.employeeNo &&
          fa.employee.employeeNo
            .toString()
            .toLowerCase()
            .includes(searchText)) ||
        (fa.applicationNo &&
          fa.applicationNo.toString().toLowerCase().includes(searchText)),
    );
    setFilterData(filtered);
  };

  // Handle deletion of an entire food allowance record.
  const handleDelete = (id) => {
    setDeleteTarget(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axiosInstance.delete(`/foodallowance/${deleteTarget}`);
      setFoodAllowance((prev) => prev.filter((fa) => fa._id !== deleteTarget));
      setFilterData((prev) => prev.filter((fa) => fa._id !== deleteTarget));
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
      toast.success("FoodAllowance deleted successfully");
    } catch (error) {
      toast.error("Failed to delete foodallowance");
    }
  };

  // Handle year change.
  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
  };

  const handleFromMonthChange = (e) => {
    setSelectedFromMonth(e.target.value);
  };

  const handleToMonthChange = (e) => {
    setSelectedToMonth(e.target.value);
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  // const itemsPerPage = 5;
  const currentFoodAllowanceItems = [...filterData]
    .reverse()
    .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  const pageCount = Math.ceil(filterData.length / itemsPerPage);

  // Check Role.
  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));
  const userRole = authData.role;

  const updateFoodAllowanceStatus = async (faId, status) => {
    try {
      await axiosInstance.post(
        `/foodallowance/foodallowance-approval/${faId}`,
        { status },
      );
      toast.success(`FoodAllowance ${status.toLowerCase()} successfully`);
      fetchFoodAllowance();
    } catch (error) {
      toast.error(`Failed to ${status.toLowerCase()} foodallowance`);
    }
  };

  // Handle status change.
  const handleStatusChange = (faItem, action) => {
    if (faItem.status !== "Pending") {
      toast.info("Only pending foodallowances can be updated.");
      return;
    }
    setStatusFoodAllowance(faItem);
    setStatusAction(action);
    setIsStatusModalOpen(true);
  };

  const confirmStatusChange = async () => {
    await updateFoodAllowanceStatus(statusFoodAllowance._id, statusAction);
    setIsStatusModalOpen(false);
    setStatusFoodAllowance(null);
    setStatusAction(null);
  };

  const cancelStatusChange = () => {
    setIsStatusModalOpen(false);
    setStatusFoodAllowance(null);
    setStatusAction(null);
  };

  const handleExport = () => {
    const tableData = [];

    // Header
    tableData.push([
      "Sl No",
      "Date",
      "Company",
      "Employee Name",
      "Employee No",
      "Total Allowance",
      "Food Date",
      "Dish",
      "Quantity",
      "Hours",
      "Vessel Name",
      "Vessel Code",
      "Allowance",
      "Remark",
      "Amount",
      "Food Total",
      "Paid Date",
      "Paid Amount",
      "Balance",
    ]);

    const merges = [];
    let currentRow = 1; // because header is row 0

    filterData.forEach((fa, index) => {
      const foodList = fa.foodDetails || [];
      const paidList = fa.paidDetails || [];
      const maxLength = Math.max(foodList.length, paidList.length, 1);

      for (let i = 0; i < maxLength; i++) {
        const food = foodList[i] || {};
        const paid = paidList[i] || {};

        tableData.push([
          i === 0 ? index + 1 : "",
          i === 0 ? fa.filterBy || "N/A" : "",
          i === 0 ? fa.employee?.companyName || "N/A" : "",
          i === 0 ? fa.employee?.name || "N/A" : "",
          i === 0 ? fa.employee?.employeeNo || "N/A" : "",
          i === 0 ? (fa.allowanceTotal ?? 0) : "",
          food.date ? new Date(food.date).toLocaleDateString("en-GB") : "",
          food.dish || "",
          food.quantity ?? "",
          food.hours ?? "",
          food.vessal?.vessel_name || "",
          food.vessal?.vessel_code || "",
          food.allowance ?? "",
          food.remark ?? "",
          typeof food.amount === "number"
            ? food.amount.toLocaleString("en-IN")
            : (food.amount ?? ""),
          i === 0 ? (fa.foodTotal ?? 0) : "",
          paid.date ? new Date(paid.date).toLocaleDateString("en-GB") : "",
          paid.amount ?? "",
          i === 0 ? (fa.balance ?? 0) : "",
        ]);
      }

      // Set merge ranges for repeating data
      if (maxLength > 1) {
        merges.push(
          {
            s: { r: currentRow, c: 0 },
            e: { r: currentRow + maxLength - 1, c: 0 },
          }, // Sl No
          {
            s: { r: currentRow, c: 1 },
            e: { r: currentRow + maxLength - 1, c: 1 },
          }, // Date
          {
            s: { r: currentRow, c: 2 },
            e: { r: currentRow + maxLength - 1, c: 2 },
          }, // Company
          {
            s: { r: currentRow, c: 3 },
            e: { r: currentRow + maxLength - 1, c: 3 },
          }, // Employee Name
          {
            s: { r: currentRow, c: 4 },
            e: { r: currentRow + maxLength - 1, c: 4 },
          }, // Employee No
          {
            s: { r: currentRow, c: 5 },
            e: { r: currentRow + maxLength - 1, c: 5 },
          }, // Total Allowance
          {
            s: { r: currentRow, c: 15 },
            e: { r: currentRow + maxLength - 1, c: 15 },
          }, // Food Total
          {
            s: { r: currentRow, c: 18 },
            e: { r: currentRow + maxLength - 1, c: 18 },
          }, // Balance
        );
      }

      currentRow += maxLength;
    });

    const ws = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_aoa(ws, tableData);

    ws["!merges"] = merges;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FoodAllowance Report");

    XLSX.writeFile(wb, "foodallowance-report.xlsx");
  };

  const handleReport = () => {
    const tableData = [];

    // Header
    tableData.push([
      "Sl No",
      "Date",
      "company",
      "Employee Name",
      "Employee No",
      "Days",
      "Total Allowance",
      "Food Total",
      "Total Amount Paid",
      "Balance",
    ]);

    let rowCount = 1;

    filterData.forEach((fa, index) => {
      const repeatCount = fa.workedDays ?? 1;

      tableData.push([
        index + 1, // Sl No
        fa.filterBy || "N/A", // Employee Name
        fa.employee?.companyName || "N/A", // Employee Name
        fa.employee?.name || "N/A", // Employee Name
        fa.employee?.employeeNo || "N/A", // Employee No
        fa.workedDays ?? 0, // Days
        fa.allowanceTotal ?? 0, // Total Allowance
        fa.foodTotal ?? 0, // Food Total
        fa.paidTotal ?? 0, // Total Amount Paid
        fa.balance ?? 0, // Balance
      ]);
      rowCount++;
    });

    const ws = XLSX.utils.aoa_to_sheet(tableData);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FoodAllowance Report");

    XLSX.writeFile(wb, "foodallowance-report.xlsx");
  };

  function formatPrice(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return "N/A";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  const contextValue = {
    foodallowance,
    setFoodAllowance,
    filterData,
    setFilterData,
    fetchFoodAllowance,
    loading,
  };

  return (
    <FoodAllowanceContext.Provider value={contextValue}>
      <div>
        <ToastContainer />
        <div className="mb-4 headStyle d-flex  justify-content-between align-items-center">
          <h4 className="mb-0">FoodAllowance</h4>
          <button
            className="button-2"
            onClick={() => {
              if (selectedCompany?.status === false) {
                toast.error(
                  "The selected company is inactive. Cannot add a foodallowance.",
                );
              } else {
                setSelectedData(null);
                setShowForm(true);
              }
            }}
            style={{
              borderRadius: "8px",
              whiteSpace: "nowrap",
              padding: "8px 16px",
            }}
          >
            Add FoodAllowance
          </button>
        </div>
        <div className="headStyle">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <select
                className="form-select"
                value={selectedYear}
                onChange={handleYearChange}
                style={{ width: "90px" }}
              >
                <option value="">Year</option>
                {Array.from(
                  { length: new Date().getFullYear() - 2000 + 1 },
                  (_, i) => 2000 + i,
                ).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                className="form-select"
                value={selectedFromMonth}
                onChange={handleFromMonthChange}
                style={{ width: "150px" }}
              >
                <option value="">From Month</option>
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                className="form-select"
                value={selectedToMonth}
                onChange={handleToMonthChange}
                style={{ width: "150px" }}
              >
                <option value="">To Month</option>
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="headStyle mt-4 ">
          <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <div>
                <input
                  className="form-control"
                  type="search"
                  placeholder="Search..."
                  style={{
                    maxWidth: "100%",
                    border: "1px solid #1E2A5E",
                    borderRadius: "8px",
                  }}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="d-flex align-items-center">
                <label className="me-2 pt-1">Count</label>
                <select
                  className="form-select no-arrow"
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  style={{ width: "auto" }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                  <option value="25">25</option>
                  <option value="30">30</option>
                </select>
              </div>
            </div>
            <div>
              <button
                className="button-2 exxportBtn me-3"
                onClick={handleExport}
              >
                Export to Excel
              </button>

              <button className="button-2 button-4" onClick={handleReport}>
                Report
              </button>
            </div>
          </div>
          <div
            className="my-3 rounded table-responsive-sm"
            style={{ overflowX: "auto" }}
          >
            <table id="my-table" className="table table-bordered mb-0">
              <thead className="t_head">
                <tr className="text-center align-middle">
                  <th className="align-middle">Sl No</th>
                  <th className="align-middle">Employee Name</th>
                  <th className="align-middle">Employee No</th>
                  <th className="align-middle">Days</th>
                  <th className="align-middle">Total Allowance</th>
                  <th className="align-middle">Food Total</th>
                  <th className="align-middle">Total Amount Paid</th>
                  <th className="align-middle">Balance</th>
                  <th className="align-middle">Payment</th>
                  <th className="align-middle">Report</th>
                  {["superadmin"].includes(userRole) && (
                    <th className="text-center align-middle">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="15">
                      <div className="d-flex justify-content-center m-3">
                        <div className="spinner"></div>
                      </div>
                    </td>
                  </tr>
                ) : currentFoodAllowanceItems.length === 0 ? (
                  <tr>
                    <td colSpan="15">
                      <div className="d-flex justify-content-center m-1">
                        <h6>Data Not Found</h6>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentFoodAllowanceItems.map((faItem, index) => {
                    return (
                      <tr key={faItem._id || index}>
                        <td>{currentPage * itemsPerPage + index + 1}</td>
                        <td>{faItem.employee?.name || "N/A"}</td>
                        <td>{faItem.employee?.employeeNo || "N/A"}</td>
                        <td>{faItem.workedDays || "N/A"}</td>
                        <td>$ {formatPrice(faItem.allowanceTotal) || "00"}</td>
                        <td>$ {formatPrice(faItem.foodTotal) || "00"}</td>
                        <td>$ {formatPrice(faItem.paidTotal) || "0"}</td>
                        <td>$ {formatPrice(faItem.balance) || "00"}</td>
                        <td className="text-center">
                          <button
                            className="button-2 addBtn me-2"
                            onClick={() => {
                              setSelectedData(faItem);
                              setShowPaymentForm(true);
                            }}
                          >
                            Add
                          </button>
                          <button
                            className="button-2 viewBtn"
                            onClick={() => {
                              setSelectedData(faItem);
                              setShowPaymentReport(true);
                            }}
                          >
                            View
                          </button>
                        </td>
                        <td className="text-center ">
                          <button
                            className="button-2 viewReportBtn"
                            onClick={() => {
                              setSelectedData(faItem);
                              setShowReport(true);
                            }}
                          >
                            View Report
                          </button>
                        </td>

                        {["superadmin"].includes(userRole) && (
                          <>
                            <td>
                              <div className="d-flex justify-content-center align-items-center gap-2">
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => {
                                    setDeleteTarget(faItem._id);
                                    setIsDeleteModalOpen(true);
                                  }}
                                >
                                  <i className="bi bi-trash3"></i>
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <ReactPaginate
            breakLabel="..."
            nextLabel="next >"
            onPageChange={handlePageClick}
            pageRangeDisplayed={5}
            pageCount={pageCount}
            previousLabel="< previous"
            renderOnZeroPageCount={null}
            containerClassName="pagination justify-content-center pagination-sm"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            activeClassName="active"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            disabledClassName="disabled"
          />
          {isViewMode && (
            <FoodAllowanceView
              foodallowance={selectedData}
              isViewMode={isViewMode}
              userRole={userRole}
              setIsViewMode={setIsViewMode}
            />
          )}
          {showForm && (
            <FoodAllowanceForm
              companyCode={companyCode}
              companyId={companyId}
              employee={employee}
              selectedCompany={selectedCompany}
              onClose={() => {
                setShowForm(false);
                fetchFoodAllowance();
              }}
            />
          )}
          {showPaymentForm && (
            <FoodAllowanceRepayment
              foodallowance={selectedData}
              companyCode={companyCode}
              companyId={companyId}
              employee={employee}
              selectedCompany={selectedCompany}
              onClose={() => {
                setShowPaymentForm(false);
                fetchFoodAllowance();
              }}
            />
          )}
          {showReport && (
            <ViewReport
              foodallowance={selectedData}
              companyCode={companyCode}
              companyId={companyId}
              employee={employee}
              formatPrice={formatPrice}
              selectedCompany={selectedCompany}
              onClose={() => {
                setShowReport(false);
                fetchFoodAllowance();
              }}
            />
          )}
          {showPaymentReport && (
            <FoodPaymentReport
              foodallowance={selectedData}
              companyCode={companyCode}
              companyId={companyId}
              employee={employee}
              formatPrice={formatPrice}
              selectedCompany={selectedCompany}
              onClose={() => {
                setShowPaymentReport(false);
                fetchFoodAllowance();
              }}
            />
          )}

          {/* Status Confirmation Modal */}
          {isStatusModalOpen && (
            <div
              className={`modal fade ${isStatusModalOpen ? "show d-block" : ""}`}
              style={{
                display: "block",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              }}
              tabIndex="-1"
              role="dialog"
              aria-labelledby="statusModalLabel"
              aria-hidden={!isStatusModalOpen}
              onClick={(e) => {
                if (e.target.classList.contains("modal")) cancelStatusChange();
              }}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="statusModalLabel">
                      Confirm {statusAction} FoodAllowance
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={cancelStatusChange}
                    ></button>
                  </div>
                  <div className="modal-body modalWhite">
                    Are you sure you want to {statusAction.toLowerCase()} this
                    foodallowance? This action cannot be undone.
                  </div>
                  <div className="modal-footer">
                    <button className="button-2" onClick={cancelStatusChange}>
                      Cancel
                    </button>
                    <button className="button-2" onClick={confirmStatusChange}>
                      {statusAction}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {isDeleteModalOpen && (
            <div
              className={`modal fade ${isDeleteModalOpen ? "show d-block" : ""}`}
              style={{
                display: "block",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              }}
              tabIndex="-1"
              role="dialog"
              aria-labelledby="deleteModalLabel"
              aria-hidden={!isDeleteModalOpen}
              onClick={(e) => {
                if (e.target.classList.contains("modal"))
                  setIsDeleteModalOpen(false);
              }}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="deleteModalLabel">
                      Confirm Deletion
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setIsDeleteModalOpen(false)}
                    ></button>
                  </div>
                  <div className="modal-body modalWhite">
                    Are you sure you want to delete this foodallowance? This
                    action cannot be undone.
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setIsDeleteModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button className="btn btn-danger" onClick={confirmDelete}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* <FoodAllowanceContext.Provider
      value={{
        foodallowance,
        setFoodAllowance,
        fetchFoodAllowance,
        filterData,
        setFilterData,
        loading
      }}
    >
      {children}
    </FoodAllowanceContext.Provider> */}
          <ToastContainer />
        </div>
      </div>
    </FoodAllowanceContext.Provider>
  );
};

export default FoodAllowanceList;

export const useFoodAllowanceContext = () => useContext(FoodAllowanceContext);
